<script>
import Layout from "@/router/layouts/main";
import { api } from "@/state/services";
import { mapState } from "vuex";
import { regionalUserMenus } from "@/utils/regional-user-permissions";
import { required, email, minLength } from "vuelidate/lib/validators";
import { VclList } from "vue-content-loading";

export default {
  components: {
    Layout,
    VclList,
  },
  data() {
    return {
      loading: true,
      saving: false,
      errored: false,
      users: [],
      menus: regionalUserMenus,
      fields: [
        { key: "actions", label: "" },
        { key: "name", label: "Nome", sortable: true },
        { key: "email", label: "E-mail", sortable: true },
        { key: "document", label: "CPF", sortable: true },
        { key: "status", label: "Status", sortable: true },
      ],
      filter: "",
      modal: false,
      submitted: false,
      form: this.emptyForm(),
      del: {
        modal: false,
        id: null,
      },
    };
  },
  computed: {
    ...mapState({
      account: (state) => state.account,
    }),
    // Só apresentação: o catálogo continua plano e o que se salva em `permissions` continua sendo
    // a lista de paths. Os rótulos seguem o padrão "Grupo - Item" (routes-list.js); o que não tem
    // grupo fica em "Geral". A ordem dos grupos é a da primeira aparição no catálogo.
    menuGroups() {
      const groups = [];
      const byName = {};
      this.menus.forEach((menu) => {
        const sep = menu.label.indexOf(" - ");
        const name = sep > -1 ? menu.label.slice(0, sep) : "Geral";
        const label = sep > -1 ? menu.label.slice(sep + 3) : menu.label;
        if (!byName[name]) {
          byName[name] = { name, items: [] };
          groups.push(byName[name]);
        }
        byName[name].items.push({ key: menu.key, label });
      });
      return groups;
    },
  },
  validations() {
    const passwordRules = this.form.id ? {} : { required, minLength: minLength(6) };

    return {
      form: {
        name: { required },
        email: { required, email },
        document: { required },
        password: passwordRules,
      },
    };
  },
  methods: {
    emptyForm() {
      return {
        id: null,
        name: "",
        email: "",
        document: "",
        password: "",
        status: "active",
        permissions: [],
      };
    },
    getUsers() {
      this.loading = true;

      api
        .get("regional-users")
        .then((response) => {
          if (response.data.status == "success") {
            this.users = response.data.list;
          } else {
            this.$toast.error(response.data.message || "Nao foi possivel carregar os usuarios.");
          }
        })
        .catch((error) => {
          this.errored = error;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    openCreate() {
      this.form = this.emptyForm();
      this.submitted = false;
      this.modal = true;
      this.$nextTick(() => this.$v.$reset());
    },
    openEdit(user) {
      this.form = {
        id: user.id,
        name: user.name,
        email: user.email,
        document: user.document,
        password: "",
        status: user.status,
        permissions: user.permissions ? [...user.permissions] : [],
      };
      this.submitted = false;
      this.modal = true;
      this.$nextTick(() => this.$v.$reset());
    },
    toggleAllPermissions(checked) {
      this.form.permissions = checked ? this.menus.map((menu) => menu.key) : [];
    },
    saveUser() {
      this.submitted = true;
      this.$v.$touch();

      if (this.$v.$invalid) {
        return;
      }

      this.saving = true;
      const endpoint = this.form.id ? "regional-users/" + this.form.id : "regional-users";

      api
        .post(endpoint, this.form)
        .then((response) => {
          if (response.data.status == "success") {
            this.$toast.success(response.data.message);
            this.modal = false;
            this.getUsers();
          } else {
            this.$toast.error(response.data.message || "Nao foi possivel salvar o usuario.");
          }
        })
        .catch(() => {
          this.$toast.error("Nao foi possivel salvar o usuario.");
        })
        .finally(() => {
          this.saving = false;
        });
    },
    showDelete(id) {
      this.del.id = id;
      this.del.modal = true;
    },
    deleteUser() {
      api.delete("regional-users/" + this.del.id).then((response) => {
        if (response.data.status == "success") {
          this.$toast.success(response.data.message);
          this.getUsers();
        } else {
          this.$toast.error(response.data.message || "Nao foi possivel excluir o usuario.");
        }

        this.del.modal = false;
        this.del.id = null;
      });
    },
  },
  mounted() {
    this.getUsers();
  },
};
</script>

<template>
  <Layout>
    <div class="page-title-box d-md-flex justify-content-between mb-3">
      <div>
        <h3>Usuários</h3>
        <p>Gerencie acessos vinculados a sua regional.</p>
      </div>
    </div>

    <div class="filter-bar d-flex mb-4">
      <div class="totals">
        <h6 class="m-0">{{ users.length }}</h6>
        <h6 class="m-0 text-secondary">usuarios vinculados</h6>
      </div>
      <b-button variant="default" @click="openCreate">Cadastrar usuario</b-button>
    </div>

    <div class="card">
      <div class="card-body min-vh-25">
        <div v-if="errored">
          Pedimos desculpas, nao podemos recuperar informacoes neste momento. Por favor, tente novamente mais tarde.
        </div>
        <vcl-list v-else-if="loading" class="col-md-6"></vcl-list>
        <div v-else-if="users.length == 0" class="text-center">
          Nenhum usuario vinculado encontrado.
        </div>
        <div v-else class="table-responsive">
          <b-input v-model="filter" placeholder="Busca" class="mb-4"></b-input>
          <b-table stacked="md" :items="users" :fields="fields" :filter="filter" class="table-nowrap table-style table-hover" responsive>
            <template #cell(actions)="row">
              <b-dropdown variant="more">
                <template #button-content>
                  <i class="fas fa-ellipsis-h"></i>
                </template>
                <b-dropdown-item @click="openEdit(row.item)"><i class="bx bx-pencil"></i> editar</b-dropdown-item>
                <b-dropdown-item @click="showDelete(row.item.id)"><i class="bx bx-trash"></i> excluir</b-dropdown-item>
              </b-dropdown>
            </template>
            <template #cell(status)="row">
              <span class="badge" :class="row.item.status == 'active' ? 'badge-success' : 'badge-secondary'">
                {{ row.item.status == "active" ? "Ativo" : "Inativo" }}
              </span>
            </template>
          </b-table>
        </div>
      </div>
    </div>

    <b-modal v-model="modal" :title="form.id ? 'Editar usuario' : 'Cadastrar usuario'" modal-class="modal-right" hide-footer>
      <b-form @submit.prevent="saveUser">
        <b-form-group label="Nome" label-for="name">
          <b-form-input id="name" v-model="form.name" :class="{ 'is-invalid': submitted && $v.form.name.$error }"></b-form-input>
          <div v-if="submitted && !$v.form.name.required" class="invalid-feedback">O nome e obrigatorio.</div>
        </b-form-group>

        <b-form-group label="E-mail" label-for="email">
          <b-form-input id="email" v-model="form.email" type="email" :class="{ 'is-invalid': submitted && $v.form.email.$error }"></b-form-input>
          <div v-if="submitted && $v.form.email.$error" class="invalid-feedback">Informe um e-mail valido.</div>
        </b-form-group>

        <b-form-group label="CPF" label-for="document">
          <b-form-input id="document" v-model="form.document" v-mask="'###.###.###-##'" :class="{ 'is-invalid': submitted && $v.form.document.$error }"></b-form-input>
          <div v-if="submitted && !$v.form.document.required" class="invalid-feedback">O CPF e obrigatorio.</div>
        </b-form-group>

        <b-form-group :label="form.id ? 'Nova senha' : 'Senha'" label-for="password">
          <b-form-input id="password" v-model="form.password" type="password" :class="{ 'is-invalid': submitted && $v.form.password.$error }"></b-form-input>
          <small v-if="form.id" class="form-text text-muted">Deixe em branco para manter a senha atual.</small>
          <div v-if="submitted && $v.form.password.$error" class="invalid-feedback">A senha precisa ter pelo menos 6 caracteres.</div>
        </b-form-group>

        <b-form-group label="Status" label-for="status">
          <b-form-select
            id="status"
            v-model="form.status"
            :options="[
              { value: 'active', text: 'Ativo' },
              { value: 'inactive', text: 'Inativo' },
            ]"
          ></b-form-select>
        </b-form-group>

        <div class="d-flex justify-content-between align-items-center mb-2">
          <h5 class="mb-0">Permissoes de menu</h5>
          <b-form-checkbox
            :checked="form.permissions.length === menus.length"
            @change="toggleAllPermissions"
          >
            Todos
          </b-form-checkbox>
        </div>

        <div class="permission-list mb-4">
          <fieldset v-for="group in menuGroups" :key="group.name" class="permission-group">
            <legend class="permission-group-title">{{ group.name }}</legend>
            <b-form-checkbox-group v-model="form.permissions" stacked>
              <b-form-checkbox v-for="menu in group.items" :key="menu.key" :value="menu.key">
                {{ menu.label }}
              </b-form-checkbox>
            </b-form-checkbox-group>
          </fieldset>
        </div>

        <b-button :disabled="saving" type="submit" variant="default">
          Salvar
          <b-spinner v-if="saving" small class="ml-2 align-middle" variant="white" role="status"></b-spinner>
        </b-button>
      </b-form>
    </b-modal>

    <b-modal v-model="del.modal" modal-class="modal-bottom" hide-header hide-footer>
      <div>
        <h4>Excluir usuario</h4>
        <p>Confirma exclusao deste usuario vinculado?</p>
        <div class="d-flex">
          <button class="btn btn-default mr-3" v-on:click="deleteUser">Confirmar</button>
          <button class="btn btn-outline-link" v-on:click="del.modal = false">Fechar <sup>ESC</sup></button>
        </div>
      </div>
    </b-modal>
  </Layout>
</template>

<style scoped>
.permission-list {
  max-height: 320px;
  overflow: auto;
  border: 1px solid #d2d2e4;
  border-radius: 6px;
  padding: 12px 14px;
}
.permission-group + .permission-group {
  margin-top: 12px;
  padding-top: 10px;
  border-top: 1px solid var(--st-border-soft);
}
.permission-group-title {
  width: auto;
  margin-bottom: 4px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--st-text-muted);
}
</style>
